import { useState, type ReactNode } from 'react';

import { Box, Layers, Lock, Plus, Trash2, TriangleAlert } from 'lucide-react';

import { useMyPermissions } from '@/api/getMyPermissions';
import { PermissionCheck } from '@/components/permissionCheck';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DateDisplay } from '@/components/ui/date-display';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import {
  Permissions,
  WILDCARD,
  hasPermission,
  type MyRoleGrant,
} from '@/models/permission';
import { type User } from '@/models/user';

import { GrantRoleDialog } from './GrantRoleDialog';
import { useRoles } from '../api/getRoles';
import { useUser } from '../api/getUser';
import { useGrantRole, useRevokeRole } from '../api/useRoleGrants';
import { isAllSandboxes, scopeLabel } from '../models/member';
import {
  isLastManagementGrant,
  managementScope,
  managementScopeLabel,
} from '../models/myPermissions';

export const initials = (displayName: string) =>
  displayName
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

const MetaCard = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Card className="gap-1 p-3">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm">{children}</span>
  </Card>
);

/**
 * The viewer's own administration boundary, resolved once per sheet.
 *
 * `removalReason` is the single mechanism for every "not yours to remove" case: an
 * empty string means the Grant can be removed, anything else is the explanation
 * shown instead of removing it.
 */
const useAdministration = (gameCode: string, subject: User | null) => {
  const { data: me } = useMyPermissions();
  const { data: rolesData, isLoading: rolesLoading } = useRoles();

  const myGrants = me?.roles ?? [];
  const scope = managementScope(myGrants, gameCode);
  // The profile read finally carries a numeric id, so "is this me" is an id
  // comparison rather than a lowercased email join.
  const isSelf = subject !== null && me?.id === subject.id;

  // A Grant names its Role but does not identify it, and revoking keys on the
  // UUID. The catalogue is the only place that pairs the two.
  const roleIdByName = new Map(
    (rolesData?.roles ?? []).map((role) => [role.name, role.id]),
  );

  const removalReason = (grant: MyRoleGrant) => {
    if (!roleIdByName.has(grant.role)) {
      return `${grant.role} is not in the Role catalogue, so this Grant cannot be identified for removal.`;
    }

    const inScope = hasPermission(
      myGrants,
      Permissions.admin.managePermissions,
      grant.game_code,
      grant.sandbox_code,
    );

    if (inScope) {
      // #419 AC 9. admin-api refuses this too, so the UI is the explanation, not
      // the rule.
      return isSelf && isLastManagementGrant(myGrants, grant)
        ? `This is your last ${grant.role} Grant. Removing it would lock you out of access administration.`
        : '';
    }

    // ponytail: the mockups have no cross-Game Grant, so this sentence is ours —
    // a ('*', '*') Grant is administered by a Nacon-wide manager only, and
    // "outside the Sandboxes you manage" would misdescribe it.
    if (grant.game_code === WILDCARD) {
      return 'This Grant covers every Game. Only a Nacon-wide Permission Manager can remove it.';
    }

    // The Game, not the Sandbox, is what is out of reach here: naming the Sandbox
    // scope would read as a contradiction to a Game-wide manager of another Game.
    if (grant.game_code !== gameCode) {
      return `This Grant is on ${grant.game_code}. You administer access on ${gameCode} only.`;
    }

    return `Outside your scope — you manage ${managementScopeLabel(scope)} only.`;
  };

  return {
    // The Role catalogue is a second query: until it lands, roleIdByName is empty
    // and every removalReason would blame the catalogue. The sheet already has a
    // loading branch — this keeps it up until both reads are in.
    rolesLoading,
    roleId: (grant: MyRoleGrant) => roleIdByName.get(grant.role) ?? '',
    scopeNote: scope.gameWide
      ? `You can grant on any Sandbox of ${gameCode}, or on All Sandboxes.`
      : `Every Grant of this user is listed. You can only add or remove Grants scoped to ${scope.sandboxCodes.join(', ')} — Grants on other Sandboxes, on All Sandboxes, or on another Game are read-only for you.`,
    removalReason,
  };
};

const GrantScopeCell = ({ grant }: { grant: MyRoleGrant }) => (
  <span
    className={cn(
      'flex items-center gap-1',
      isAllSandboxes(grant) && 'text-primary font-medium',
    )}
  >
    {isAllSandboxes(grant) ? <Layers size={14} /> : <Box size={14} />}
    {scopeLabel(grant)}
  </span>
);

// Locked stays clickable on purpose: a disabled button can only carry a tooltip,
// and the reason a Grant is not yours to remove needs a sentence. Both buttons run
// the same handler — the reason is what decides whether it removes or explains.
const GrantActionCell = ({
  grant,
  reason,
  onRemove,
}: {
  grant: MyRoleGrant;
  reason: string;
  onRemove: (grant: MyRoleGrant) => void;
}) => (
  <Button
    variant="ghost"
    size="sm"
    className={cn(reason !== '' && 'text-muted-foreground')}
    onClick={() => {
      onRemove(grant);
    }}
  >
    {reason === '' ? (
      <>
        <Trash2 />
        Remove
      </>
    ) : (
      <>
        <Lock />
        Locked
      </>
    )}
  </Button>
);

const RoleGrantsCard = ({
  grants,
  gameCode,
  scopeNote,
  isLoading,
  isError,
  removalReason,
  onAdd,
  onRemove,
}: {
  grants: MyRoleGrant[];
  gameCode: string;
  scopeNote: string;
  isLoading: boolean;
  isError: boolean;
  removalReason: (grant: MyRoleGrant) => string;
  onAdd: () => void;
  onRemove: (grant: MyRoleGrant) => void;
}) => (
  <Card className="gap-2 p-3">
    <div className="flex items-center gap-3">
      <span className="flex-1 text-sm font-medium">Role Grants</span>
      <PermissionCheck
        scope="game"
        gameCode={gameCode}
        allOf={[Permissions.admin.managePermissions]}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={onAdd}
          disabled={isLoading || isError}
        >
          <Plus />
          Add Role Grant
        </Button>
      </PermissionCheck>
    </div>

    {isLoading && (
      <Text variant="muted">Reading this user&apos;s Grants...</Text>
    )}

    {/* An unreadable list is not an empty one: saying "holds no Grant" here would
        be a wrong answer that looks like a right one. */}
    {isError && (
      <Text variant="muted">
        This user&apos;s Grants could not be read, so none is listed. That says
        nothing about what they hold.
      </Text>
    )}

    {!isLoading && !isError && grants.length === 0 && (
      <Text variant="muted">This user holds no Role Grant.</Text>
    )}

    {!isLoading && !isError && grants.length > 0 && (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Role</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Game</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {grants.map((grant) => (
            <TableRow
              key={`${grant.role}-${grant.game_code}-${grant.sandbox_code}`}
            >
              <TableCell className="font-mono">{grant.role}</TableCell>
              <TableCell>
                <GrantScopeCell grant={grant} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {grant.game_code === WILDCARD ? 'Every Game' : grant.game_code}
              </TableCell>
              <TableCell>
                <GrantActionCell
                  grant={grant}
                  reason={removalReason(grant)}
                  onRemove={onRemove}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}

    <p className="border-t pt-2 text-xs text-muted-foreground">{scopeNote}</p>
  </Card>
);

// What the sheet is asking to confirm. 'blocked' is the odd one out: it confirms
// nothing, it explains why the removal the viewer just asked for is unavailable.
type Confirmation =
  | { kind: 'removeGrant'; grant: MyRoleGrant }
  | { kind: 'blocked'; reason: string };

const ConfirmationDialog = ({
  confirmation,
  subject,
  isPending,
  onOpenChange,
  onConfirm,
}: {
  confirmation: Confirmation | null;
  subject: User;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (grant: MyRoleGrant) => void;
}) => {
  if (!confirmation) return null;

  const blocked = confirmation.kind === 'blocked';

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogMedia>
            {blocked ? <Lock /> : <TriangleAlert />}
          </AlertDialogMedia>
          <AlertDialogTitle>
            {blocked
              ? 'This removal is not available'
              : 'Remove this Role Grant?'}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {confirmation.kind === 'blocked'
              ? confirmation.reason
              : `${confirmation.grant.role} on ${scopeLabel(confirmation.grant)} will be removed from ${subject.email}. Their other Grants are unaffected.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* Nothing to confirm on a blocked removal, so the only way out reads
              as an acknowledgement rather than as abandoning an edit — and it
              spans the footer's two columns rather than leaving a gap where the
              action would have been. */}
          <AlertDialogCancel className={cn(blocked && 'col-span-2')}>
            {blocked ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          {confirmation.kind === 'removeGrant' && (
            <AlertDialogAction
              variant="destructive"
              disabled={isPending}
              onClick={() => {
                onConfirm(confirmation.grant);
              }}
            >
              Remove Grant
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

interface MemberSheetProps {
  member: User | null;
  gameCode: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * One user of the directory, with every Role Grant they hold and the two things
 * that can be done to them: granting a Role on a scope, and revoking one.
 *
 * Grants are listed across every Game, unfiltered — that is what
 * `GET /v1/users/{userId}` returns, and deciding whether a Grant is the viewer's
 * to remove needs the ones outside their scope just as much as the ones inside.
 */
export const MemberSheet = ({
  member,
  gameCode,
  open,
  onOpenChange,
}: MemberSheetProps) => {
  const detail = useUser(member?.id ?? null);
  const grantRole = useGrantRole(gameCode);
  const revokeRole = useRevokeRole(gameCode);
  const { roleId, scopeNote, removalReason, rolesLoading } = useAdministration(
    gameCode,
    member,
  );
  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  const grants = detail.data?.roles ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-170 gap-4">
        <SheetHeader className="p-0">
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium">
              {initials(member?.display_name ?? '')}
            </div>
            <div className="min-w-0">
              <SheetTitle>{member?.email}</SheetTitle>
              <SheetDescription>{member?.display_name}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {member && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <MetaCard label="Login">{member.login || '—'}</MetaCard>
              <MetaCard label="Job title">{member.job_title || '—'}</MetaCard>
              <MetaCard label="Last sign-in">
                {member.last_login ? (
                  <DateDisplay
                    date={member.last_login}
                    displayFormat="relative"
                  />
                ) : (
                  'Never'
                )}
              </MetaCard>
            </div>

            <div className="flex-1 overflow-y-auto">
              <RoleGrantsCard
                grants={grants}
                gameCode={gameCode}
                scopeNote={scopeNote}
                isLoading={detail.isLoading || rolesLoading}
                isError={detail.isError}
                removalReason={removalReason}
                onAdd={() => {
                  setGrantOpen(true);
                }}
                onRemove={(grant) => {
                  const reason = removalReason(grant);
                  setConfirmation(
                    reason === ''
                      ? { kind: 'removeGrant', grant }
                      : { kind: 'blocked', reason },
                  );
                }}
              />
            </div>

            {/* Mounted only while open, so the picker starts from a clean
                selection on every opening. */}
            {grantOpen && (
              <GrantRoleDialog
                member={member}
                grants={grants}
                gameCode={gameCode}
                open
                onOpenChange={setGrantOpen}
                isPending={grantRole.isPending}
                onSubmit={({ role_id, sandbox_code }) => {
                  grantRole.mutate(
                    {
                      user_id: member.id,
                      role_id,
                      game_code: gameCode,
                      sandbox_code,
                    },
                    {
                      onSuccess: () => {
                        setGrantOpen(false);
                      },
                    },
                  );
                }}
              />
            )}

            <ConfirmationDialog
              confirmation={confirmation}
              subject={member}
              isPending={revokeRole.isPending}
              onOpenChange={(next) => {
                if (!next) setConfirmation(null);
              }}
              // Closed on success only, so isPending can actually disable the
              // button and a failed mutation leaves the dialog to explain itself.
              onConfirm={(grant) => {
                revokeRole.mutate(
                  {
                    user_id: member.id,
                    role_id: roleId(grant),
                    game_code: grant.game_code,
                    sandbox_code: grant.sandbox_code,
                  },
                  {
                    onSuccess: () => {
                      setConfirmation(null);
                    },
                  },
                );
              }}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
